import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API calls
export const authApi = {
  login: (email, password) => 
    apiClient.post('/v1/auth/login', { email, password }),
  
  register: (name, email, password) => 
    apiClient.post('/v1/auth/register', { name, email, password }),
  
  getMe: () => 
    apiClient.get('/v1/auth/me'),
};

// Admin API calls
export const adminApi = {
  login: (secret) => ({ secret }),
  
  getStats: (secret) => 
    apiClient.get('/v1/admin/stats', {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  getUsers: (secret, page = 1, limit = 50, status = null, search = null, sortBy = 'created_at', order = 'desc') => {
    let url = `/v1/admin/users?page=${page}&limit=${limit}&sort_by=${sortBy}&order=${order}`;
    if (status) url += `&status=${status}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return apiClient.get(url, {
      headers: { 'X-Admin-Secret': secret },
    });
  },
  
  getUserDetails: (secret, apiKey) =>
    apiClient.get(`/v1/admin/users/${apiKey}/details`, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  createUser: (secret, userData) => 
    apiClient.post('/v1/admin/users', userData, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  updateUser: (secret, apiKey, userData) =>
    apiClient.patch(`/v1/admin/users/${apiKey}`, userData, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  toggleUser: (secret, apiKey) => 
    apiClient.patch(`/v1/admin/users/${apiKey}/toggle`, {}, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  regenerateApiKey: (secret, apiKey) =>
    apiClient.post(`/v1/admin/users/${apiKey}/regenerate-key`, {}, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  deleteUser: (secret, apiKey) => 
    apiClient.delete(`/v1/admin/users/${apiKey}`, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  bulkUpdateUsers: (secret, apiKeys, action) =>
    apiClient.patch('/v1/admin/users/bulk-update', {
      api_keys: apiKeys,
      action: action
    }, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  bulkDeleteUsers: (secret, apiKeys, confirmationToken) =>
    apiClient.delete('/v1/admin/users/bulk-delete', {
      data: {
        api_keys: apiKeys,
        confirmation_token: confirmationToken
      },
      headers: { 'X-Admin-Secret': secret },
    }),
  
  exportUsers: (secret, format = 'csv', status = null) => {
    let url = `/v1/admin/users/export?format=${format}`;
    if (status) url += `&status=${status}`;
    return apiClient.get(url, {
      headers: { 'X-Admin-Secret': secret },
      responseType: format === 'csv' ? 'blob' : 'json'
    });
  },
  
  getLogs: (secret, userId = null, page = 1, limit = 100) => {
    let url = `/v1/admin/logs?page=${page}&limit=${limit}`;
    if (userId) url += `&user_id=${userId}`;
    return apiClient.get(url, {
      headers: { 'X-Admin-Secret': secret },
    });
  },
  
  getActionLogs: (secret, page = 1, limit = 50, action = null) => {
    let url = `/v1/admin/action-logs?page=${page}&limit=${limit}`;
    if (action) url += `&action=${encodeURIComponent(action)}`;
    return apiClient.get(url, {
      headers: { 'X-Admin-Secret': secret },
    });
  },
  
  // Analytics endpoints
  getAnalyticsOverview: (secret) =>
    apiClient.get('/v1/admin/analytics/overview', {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  getAnalyticsRequests: (secret, period = 'day', limit = 24) =>
    apiClient.get(`/v1/admin/analytics/requests?period=${period}&limit=${limit}`, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  getAnalyticsEndpoints: (secret) =>
    apiClient.get('/v1/admin/analytics/endpoints', {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  getAnalyticsUser: (secret, apiKey, period = 'week') =>
    apiClient.get(`/v1/admin/analytics/users/${apiKey}?period=${period}`, {
      headers: { 'X-Admin-Secret': secret },
    }),
  
  exportAnalytics: (secret, startDate = null, endDate = null, apiKey = null) => {
    let url = '/v1/admin/analytics/export?';
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    if (apiKey) url += `api_key=${apiKey}`;
    return apiClient.get(url, {
      headers: { 'X-Admin-Secret': secret },
    });
  },
};

// Public API calls
export const publicApi = {
  health: () => apiClient.get('/v1/health'),
};

// Location API calls (requires JWT token)
export const locationApi = {
  getProvinces: () => 
    apiClient.get('/v1/province'),
  
  getCities: (provinceId = null) => {
    let url = '/v1/city';
    if (provinceId) url += `?province_id=${provinceId}`;
    return apiClient.get(url);
  },
  
  getDistricts: (cityId = null) => {
    let url = '/v1/district';
    if (cityId) url += `?city_id=${cityId}`;
    return apiClient.get(url);
  },
  
  getSubdistricts: (districtId = null) => {
    let url = '/v1/subdistrict';
    if (districtId) url += `?district_id=${districtId}`;
    return apiClient.get(url);
  },
};

// Shipping API calls (requires JWT token)
export const shippingApi = {
  calculateCost: (data) => 
    apiClient.post('/v1/cost', data),
};